import { useMutation, useQuery } from "@apollo/react-hooks";
import styled from "@emotion/styled";
import { faGripVertical, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { gql } from "apollo-boost";
import React, { FC, useState } from "react";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";
import BlockContainer from "../../components/BlockContainer";
import BlockHeading from "../../components/BlockHeading";
import CSAButton from "../../components/CSAButton";
import IconButton from "../../components/IconButton";
import Notification, { NotificationProps } from "../../components/Notification";
import { CurrentUser } from "../../queries";

const TASK_TRACKER_ITEMS = gql`
  query TaskTrackerItems {
    taskTrackerItems {
      id
      listType
      text
      completed
      notes
      position
    }
  }
`;

const ADD_ITEM = gql`
  mutation AddTaskTrackerItem($listType: TaskTrackerListType!, $text: String!) {
    addTaskTrackerItem(listType: $listType, text: $text) {
      code
      message
    }
  }
`;

const UPDATE_ITEM = gql`
  mutation UpdateTaskTrackerItem(
    $id: ID!
    $completed: Boolean
    $notes: String
    $text: String
  ) {
    updateTaskTrackerItem(
      id: $id
      completed: $completed
      notes: $notes
      text: $text
    ) {
      code
      message
    }
  }
`;

const DELETE_ITEM = gql`
  mutation DeleteTaskTrackerItem($id: ID!) {
    deleteTaskTrackerItem(id: $id) {
      code
      message
    }
  }
`;

const REORDER_ITEMS = gql`
  mutation ReorderTaskTrackerItems(
    $listType: TaskTrackerListType!
    $orderedIds: [ID!]!
  ) {
    reorderTaskTrackerItems(listType: $listType, orderedIds: $orderedIds) {
      code
      message
    }
  }
`;

interface Item {
  id: string;
  listType: "TASK" | "ISSUE";
  text: string;
  completed: boolean;
  notes: string | null;
  position: number;
}

const Layout = styled.div`
  display: grid;
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Heading = styled.h1`
  font-size: 1.5rem;
  margin-bottom: 2rem;
`;

const Panel = styled(BlockContainer)`
  padding: 1.5rem;
`;

const PanelHeading = styled(BlockHeading)`
  margin: 0 0 1rem;
  text-align: left;
`;

const ItemRow = styled.div<{ completed: boolean }>`
  align-items: center;
  display: flex;
  gap: 0.75rem;
  opacity: ${({ completed }) => (completed ? 0.5 : 1)};
  padding: 0.75rem 0;
  position: relative;

  &::after {
    background: var(--csa-border-3);
    bottom: 0;
    content: "";
    height: 0.0625rem;
    left: 5%;
    position: absolute;
    width: 90%;
  }
`;

const DragHandle = styled(IconButton)`
  color: var(--csa-grey);
  cursor: grab;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    cursor: grab;
  }
`;

const RowDeleteButton = styled(IconButton)`
  color: var(--csa-grey);
  flex-shrink: 0;

  &:hover {
    color: var(--csa-red);
    opacity: 1;
  }
`;

const Checkbox = styled.input`
  cursor: pointer;
  flex-shrink: 0;
  height: 1.1rem;
  width: 1.1rem;
`;

const ItemText = styled.button<{ completed: boolean }>`
  background: none;
  border: 0;
  cursor: pointer;
  flex: 1;
  font-size: 0.875rem;
  padding: 0;
  text-align: left;
  text-decoration: ${({ completed }) => (completed ? "line-through" : "none")};

  &:hover {
    color: var(--csa-blue);
  }

  &:focus {
    outline: none;
  }
`;

const TextInput = styled.input`
  border: 0.0625rem solid var(--csa-border);
  border-radius: 0.25rem;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  width: 100%;

  &:focus {
    border-color: var(--csa-blue);
    outline: none;
  }
`;

const ExpandedPanel = styled.div`
  background: var(--csa-bg-secondary);
  border-radius: 0.25rem;
  margin: 0.25rem 0 0.75rem;
  padding: 1rem;
`;

const Textarea = styled.textarea`
  border: 0.0625rem solid var(--csa-border);
  border-radius: 0.25rem;
  font-size: 0.875rem;
  min-height: 5rem;
  padding: 0.5rem;
  resize: vertical;
  width: 100%;

  &:focus {
    border-color: var(--csa-blue);
    outline: none;
  }
`;

const ExpandedFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
`;

const SaveButton = styled(CSAButton)`
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
`;

const CompletedSection = styled.div`
  margin: 1.25rem -1.5rem 0;
  overflow: hidden;
`;

const CompletedHeader = styled.div`
  align-items: center;
  background: var(--csa-bg-tertiary);
  display: flex;
  justify-content: space-between;
  padding: 0.6rem 1.5rem;
`;

const CompletedTitle = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
`;

const HideButton = styled(IconButton)`
  color: var(--csa-blue);
  font-size: 0.75rem;
  opacity: 1;

  &:hover {
    opacity: 0.75;
  }
`;

const CompletedBody = styled.div`
  background: var(--csa-bg-secondary);
  padding: 0 1.5rem;
`;

const AddRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const AddInput = styled.input`
  border: 0.0625rem solid var(--csa-border);
  border-radius: 0.25rem;
  flex: 1;
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;

  &:focus {
    border-color: var(--csa-blue);
    outline: none;
  }
`;

const AddButton = styled(CSAButton)`
  font-size: 0.875rem;
  padding: 0.5rem 1.25rem;
  white-space: nowrap;
`;

interface ListProps {
  addLabel: string;
  items: Item[];
  listId: string;
  listType: "TASK" | "ISSUE";
  readOnly: boolean;
  title: string;
  onAdd: (text: string) => void;
  onDelete: (id: string) => void;
  onReorder: (listType: "TASK" | "ISSUE", orderedIds: string[]) => void;
  onSave: (id: string, notes: string, text: string) => void;
  onToggleComplete: (id: string, completed: boolean) => void;
}

const ItemList: FC<ListProps> = ({
  addLabel,
  items,
  listId,
  listType,
  readOnly,
  title,
  onAdd,
  onDelete,
  onReorder,
  onSave,
  onToggleComplete,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [draftNotes, setDraftNotes] = useState<Record<string, string>>({});
  const [draftText, setDraftText] = useState<Record<string, string>>({});
  const [hideCompleted, setHideCompleted] = useState(false);
  const [newText, setNewText] = useState("");

  const incomplete = [...items.filter((i) => !i.completed)].sort(
    (a, b) => a.position - b.position,
  );
  const complete = [...items.filter((i) => i.completed)].sort(
    (a, b) => a.position - b.position,
  );

  const handleAdd = () => {
    const text = newText.trim();
    if (!text) return;
    onAdd(text);
    setNewText("");
  };

  const handleExpand = (item: Item) => {
    if (expandedId === item.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(item.id);
    setDraftNotes((d) => ({ ...d, [item.id]: item.notes ?? "" }));
    setDraftText((d) => ({ ...d, [item.id]: item.text }));
  };

  const handleSave = (id: string) => {
    onSave(id, draftNotes[id] ?? "", draftText[id] ?? "");
    setExpandedId(null);
  };

  const handleDragEndIncomplete = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = Array.from(incomplete);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    onReorder(
      listType,
      reordered.map((i) => i.id),
    );
  };

  const handleDragEndComplete = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = Array.from(complete);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    onReorder(
      listType,
      reordered.map((i) => i.id),
    );
  };

  const renderDraggable = (item: Item, index: number) => (
    <Draggable
      key={item.id}
      draggableId={item.id}
      index={index}
      isDragDisabled={readOnly}
    >
      {(drag) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <div ref={drag.innerRef} {...drag.draggableProps}>
          <ItemRow completed={item.completed}>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <DragHandle {...drag.dragHandleProps}>
              <FontAwesomeIcon icon={faGripVertical} />
            </DragHandle>
            <Checkbox
              checked={item.completed}
              disabled={readOnly}
              onChange={() => onToggleComplete(item.id, !item.completed)}
              type="checkbox"
            />
            <ItemText
              completed={item.completed}
              onClick={() => handleExpand(item)}
              type="button"
            >
              {item.text}
            </ItemText>
            {!readOnly && (
              <RowDeleteButton onClick={() => onDelete(item.id)} type="button">
                <FontAwesomeIcon icon={faTimes} />
              </RowDeleteButton>
            )}
          </ItemRow>
          {expandedId === item.id && (
            <ExpandedPanel>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave(item.id);
                }}
              >
                <TextInput
                  onChange={(e) => {
                    const { value } = e.target;
                    setDraftText((d) => ({ ...d, [item.id]: value }));
                  }}
                  type="text"
                  value={draftText[item.id] ?? item.text}
                />
                <Textarea
                  onChange={(e) => {
                    const { value } = e.target;
                    setDraftNotes((d) => ({ ...d, [item.id]: value }));
                  }}
                  placeholder="Write notes here..."
                  readOnly={readOnly}
                  value={draftNotes[item.id] ?? ""}
                />
                {!readOnly && (
                  <ExpandedFooter>
                    <SaveButton type="submit">Save</SaveButton>
                  </ExpandedFooter>
                )}
              </form>
            </ExpandedPanel>
          )}
        </div>
      )}
    </Draggable>
  );

  return (
    <Panel>
      <PanelHeading>{title}</PanelHeading>

      <DragDropContext onDragEnd={handleDragEndIncomplete}>
        <Droppable droppableId={`${listId}-incomplete`}>
          {(provided) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {incomplete.map((item, index) => renderDraggable(item, index))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {complete.length > 0 && (
        <CompletedSection>
          <CompletedHeader>
            <CompletedTitle>Completed ({complete.length})</CompletedTitle>
            <HideButton
              onClick={() => setHideCompleted((v) => !v)}
              type="button"
            >
              {hideCompleted ? "Show" : "Hide"}
            </HideButton>
          </CompletedHeader>
          {!hideCompleted && (
            <CompletedBody>
              <DragDropContext onDragEnd={handleDragEndComplete}>
                <Droppable droppableId={`${listId}-complete`}>
                  {(provided) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {complete.map((item, index) =>
                        renderDraggable(item, index),
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </CompletedBody>
          )}
        </CompletedSection>
      )}

      {!readOnly && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAdd();
          }}
        >
          <AddRow>
            <AddInput
              onChange={(e) => setNewText(e.target.value)}
              placeholder={`Add new ${title.toLowerCase()}...`}
              value={newText}
            />
            <AddButton type="submit">{addLabel}</AddButton>
          </AddRow>
        </form>
      )}
    </Panel>
  );
};

export interface TaskTrackerProps {
  currentUser?: CurrentUser;
}

const TaskTracker: FC<TaskTrackerProps> = ({ currentUser }) => {
  const { data } = useQuery<{ taskTrackerItems: Item[] }>(TASK_TRACKER_ITEMS, {
    fetchPolicy: "cache-and-network",
  });

  const [notification, setNotification] = useState<NotificationProps>({});

  const refetchQueries = [{ query: TASK_TRACKER_ITEMS }];

  const [addItem] = useMutation(ADD_ITEM, {
    onCompleted: () =>
      setNotification({ children: <p>Added successfully</p>, type: "success" }),
    onError: () =>
      setNotification({
        children: <p>Sorry, something went wrong</p>,
        type: "error",
      }),
    refetchQueries,
  });

  const [updateItem] = useMutation(UPDATE_ITEM, {
    onError: () =>
      setNotification({
        children: <p>Sorry, something went wrong</p>,
        type: "error",
      }),
    refetchQueries,
  });

  const [deleteItem] = useMutation(DELETE_ITEM, {
    onCompleted: () =>
      setNotification({
        children: <p>Deleted successfully</p>,
        type: "success",
      }),
    onError: () =>
      setNotification({
        children: <p>Sorry, something went wrong</p>,
        type: "error",
      }),
    refetchQueries,
  });

  const [reorderItems] = useMutation(REORDER_ITEMS, {
    onError: () =>
      setNotification({
        children: <p>Sorry, something went wrong</p>,
        type: "error",
      }),
    refetchQueries,
  });

  const items = data?.taskTrackerItems ?? [];
  const tasks = items.filter((i) => i.listType === "TASK");
  const issues = items.filter((i) => i.listType === "ISSUE");

  const readOnly = !currentUser?.active;

  const handleAdd = (listType: "TASK" | "ISSUE") => (text: string) => {
    addItem({ variables: { listType, text } });
  };

  const handleDelete = (id: string) => {
    deleteItem({ variables: { id } });
  };

  const handleToggleComplete = (id: string, completed: boolean) => {
    updateItem({ variables: { completed, id } });
  };

  const handleSave = (id: string, notes: string, text: string) => {
    updateItem({ variables: { id, notes, text } });
  };

  const handleReorder = (listType: "TASK" | "ISSUE", orderedIds: string[]) => {
    reorderItems({ variables: { listType, orderedIds } });
  };

  return (
    <>
      <Notification fadeOut float type={notification.type} width="100%">
        {notification.children}
      </Notification>
      <Heading>Task Tracker</Heading>
      <Layout>
        <ItemList
          addLabel="Add Task"
          items={tasks}
          listId="tasks"
          listType="TASK"
          onAdd={handleAdd("TASK")}
          onDelete={handleDelete}
          onReorder={handleReorder}
          onSave={handleSave}
          onToggleComplete={handleToggleComplete}
          readOnly={readOnly}
          title="Tasks"
        />
        <ItemList
          addLabel="Add Issue"
          items={issues}
          listId="issues"
          listType="ISSUE"
          onAdd={handleAdd("ISSUE")}
          onDelete={handleDelete}
          onReorder={handleReorder}
          onSave={handleSave}
          onToggleComplete={handleToggleComplete}
          readOnly={readOnly}
          title="Issues List"
        />
      </Layout>
    </>
  );
};

export default TaskTracker;
